import dayjs from "dayjs";

import {
  LICENSE_PERMISSION_IDS,
  LICENSE_EXPIRING_SOON_DAYS,
  LICENSE_STATUS,
} from "../constants/LicenseManagement.constants";

export const formatExportRow = (row, license = {}) => {
  const { name, email, companyName, lastLogin, roles } = row;
  const { name: licenseName, licenseType, licenseStatus, expiryDate } = license;
  return {
    name: name || "",
    email: email || "",
    companyName: companyName || "",
    lastLogin: lastLogin || "",
    roles: Array.isArray(roles)
      ? roles.map((role) => role.name).join(", ")
      : "",
    licenseName: licenseName || "",
    licenseType: licenseType || "",
    licenseStatus: licenseStatus || "",
    licenseExpiryDate: expiryDate || "",
  };
};

/**
 * Determines if license actions (renew/terminate) should be disabled.
 * Returns true if licenses is empty, not an array, or contains only a single license object with all fields null/empty.
 */
export const shouldDisableLicenseActions = (licenses) => {
  if (!Array.isArray(licenses) || licenses.length === 0) return true;
  if (licenses.length === 1) {
    const licenseObj = licenses[0];
    return Object.values(licenseObj).every(
      (value) => value == null || value === "",
    );
  }
  return false;
};

/**
 * Disable terminate action if all assigned licenses are Expired or Terminated.
 */
export const shouldDisableTerminateLicense = (licenses) => {
  const allLicensesExpired = licenses.every((license) => {
    if (
      license?.licenseStatus === LICENSE_STATUS.expired ||
      license?.licenseStatus === LICENSE_STATUS.terminated
    ) {
      return true;
    } else {
      return false;
    }
  });

  return allLicensesExpired;
};

/**
 * Determines the license status based on the expiry date.
 *
 * - Returns LICENSE_STATUS.expired if the expiry date is today or in the past.
 * - Returns LICENSE_STATUS.expiringSoon if the expiry date is within the next 1 to LICENSE_EXPIRING_SOON_DAYS days.
 * - Returns LICENSE_STATUS.active if the expiry date is more than LICENSE_EXPIRING_SOON_DAYS days in the future.
 *
 * @param {string|Date|dayjs.Dayjs|null|undefined} expiryDate - The expiry date to check. Can be a string, Date, dayjs object, or null/undefined.
 * @returns {string} The license status: LICENSE_STATUS.active, LICENSE_STATUS.expiringSoon, or LICENSE_STATUS.expired.
 */
export const getLicenseStatusByExpiry = (expiryDate) => {
  if (!expiryDate) return LICENSE_STATUS.expired;
  const currentDate = dayjs().startOf("day");
  const expiryDateObj = dayjs(expiryDate).startOf("day");
  const daysUntilExpiry = expiryDateObj.diff(currentDate, "day");
  if (daysUntilExpiry <= 0) {
    return LICENSE_STATUS.expired;
  } else if (daysUntilExpiry <= LICENSE_EXPIRING_SOON_DAYS) {
    return LICENSE_STATUS.expiringSoon;
  } else {
    return LICENSE_STATUS.active;
  }
};

export const groupLicensePermissionsByCategory = (licensePermissions = []) =>
  licensePermissions.reduce((acc, permission) => {
    const category = permission?.category;
    if (!category) return acc;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(permission);
    return acc;
  }, {});

const getTargetPermissionIds = (permissionIds) => {
  const normalizedPermissionIds = Array.isArray(permissionIds)
    ? permissionIds
    : [permissionIds];

  const targetPermissionIds = new Set(
    normalizedPermissionIds.filter((id) => Number.isFinite(id)),
  );

  return targetPermissionIds.size > 0 ? targetPermissionIds : null;
};

const licenseHasAnyPermission = (license, targetPermissionIds) => {
  const permissions = license?.licensePermissions;
  if (!Array.isArray(permissions)) return false;

  return permissions.some((permission) =>
    targetPermissionIds.has(Number(permission?.permissionId)),
  );
};

/**
 * Returns licenses that include at least one of the target permission ID(s), regardless of status.
 *
 * @param {Array} userLicenses - An array of license objects assigned to the user.
 * @param {number|Array<number>} permissionIds - Permission ID(s) to filter licenses by.
 * @returns {Array} Filtered licenses containing any of the target permissions.
 */
export const getLicensesWithAssignedPermission = (
  userLicenses = [],
  permissionIds,
) => {
  if (!Array.isArray(userLicenses) || permissionIds == null) {
    return [];
  }

  const targetPermissionIds = getTargetPermissionIds(permissionIds);
  if (!targetPermissionIds) {
    return [];
  }

  return userLicenses.filter((license) =>
    licenseHasAnyPermission(license, targetPermissionIds),
  );
};

/**
 * Checks whether any license in a list matches the provided license status.
 *
 * @param {Array} licenses - Licenses list to evaluate.
 * @param {string} status - Target license status.
 * @returns {boolean} True when at least one license matches the status.
 */
export const hasLicenseWithStatus = (licenses = [], status) => {
  if (!Array.isArray(licenses) || !status) {
    return false;
  }

  return licenses.some((license) => license?.licenseStatus === status);
};

/**
 * Checks whether a user has a given permission in any non-terminated & non-expired license.
 *
 * @param {Array} userLicenses - An array of license objects assigned to the user.
 * @param {number|Array<number>} permissionIds - Permission ID(s) to check for.
 * @returns {boolean} True if the user has the permission in any eligible license, false otherwise.
 */
export const hasEligibleLicensePermission = (
  userLicenses = [],
  permissionIds,
) => {
  if (!Array.isArray(userLicenses) || permissionIds == null) {
    return false;
  }

  const targetPermissionIds = getTargetPermissionIds(permissionIds);
  if (!targetPermissionIds) {
    return false;
  }

  return userLicenses.some((license) => {
    const status = license?.licenseStatus;
    const isEligibleStatus =
      status === LICENSE_STATUS.active ||
      status === LICENSE_STATUS.expiringSoon;

    if (!isEligibleStatus) return false;

    return licenseHasAnyPermission(license, targetPermissionIds);
  });
};

/**
 * Checks whether a permission exists in any assigned license, regardless of license status.
 * Use this only for visibility concerns (for example: showing disabled fields in exports/UI).
 *
 * @param {Array} userLicenses - An array of license objects assigned to the user.
 * @param {number|Array<number>} permissionIds - Permission ID(s) to check for.
 * @returns {boolean} True if permission is found in any license, false otherwise.
 */
export const hasAssignedLicensePermission = (
  userLicenses = [],
  permissionIds,
) => {
  return (
    getLicensesWithAssignedPermission(userLicenses, permissionIds).length > 0
  );
};

/**
 * Checks whether a user has a given permission in any ACTIVE license only.
 * ExpiringSoon, Expired, and Terminated are treated as non-editable.
 *
 * @param {Array} userLicenses - An array of license objects assigned to the user.
 * @param {number|Array<number>} permissionIds - Permission ID(s) to check for.
 * @returns {boolean} True if the user has the permission in any active license, false otherwise.
 */
export const hasActiveLicensePermission = (
  userLicenses = [],
  permissionIds,
) => {
  if (!Array.isArray(userLicenses) || permissionIds == null) {
    return false;
  }

  const targetPermissionIds = getTargetPermissionIds(permissionIds);
  if (!targetPermissionIds) {
    return false;
  }

  return userLicenses.some((license) => {
    const isActiveStatus = license?.licenseStatus === LICENSE_STATUS.active;
    if (!isActiveStatus) return false;
    return licenseHasAnyPermission(license, targetPermissionIds);
  });
};

export const getPermissionIdsByPermissionNames = (permissionNames = []) => {
  return permissionNames.reduce((permissionIds, permissionName) => {
    const key = String(permissionName || "").toUpperCase();
    if (!(key in LICENSE_PERMISSION_IDS)) {
      return permissionIds;
    }

    permissionIds.push(LICENSE_PERMISSION_IDS[key]);
    return permissionIds;
  }, []);
};

export const hasExpiringSoonLicenseForPermissionIds = (
  userLicenses = [],
  permissionIds = [],
) => {
  const licensesWithPermission = getLicensesWithAssignedPermission(
    userLicenses,
    permissionIds,
  );

  return hasLicenseWithStatus(
    licensesWithPermission,
    LICENSE_STATUS.expiringSoon,
  );
};
